import { DateFilter, FilterCard, PageHeader, Show, Table, TableCell } from '@/components';
import { WelletContext } from '@/context/wellet/welletContext';
import { filtersFormat } from '@/helpers/format';
import {
  useDebounce,
  useGetRestaurants,
  useLocalStorage,
  useOrderBy,
  usePagination,
} from '@/hooks';
import { useGetChannels } from '@/hooks/queries/useGetChannels';
import useGenericTranslation from '@/hooks/useGenericTranslation';
import { downloadChannels, getChannels } from '@/services';
import {
  Button,
  ButtonFilter,
  Input,
  Loading,
  Money,
  OrganizationCard,
  PageError,
} from '@/uiComponents/common';
import { defaultQueryOptions } from '@/utils/defaultQueryOptions';
import downloadFileFromResponse from '@/utils/downloadFile';
import { SearchIcon } from 'lucide-react';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useSelector } from 'react-redux';

const Channels = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'screens.channels' });
  const welletContext = useContext(WelletContext);
  const organization = useSelector((state) => state.app.selectedOrganization);
  const [value, setValue] = useState('');
  const selectedDates = useSelector((state) => state.app.selectedDates);
  const { orderBy, orderType, handleColumnClick } = useOrderBy();
  const debouncedSearch = useDebounce(value, 700);
  const dateType = useSelector((state) => state.app.dateType);

  const [filterLocalStorage, setFilterLocalStorage] = useLocalStorage('filter-channels', {
    filterField: '',
    location: [],
    channels: [],
    page: 1,
  });

  const { currentPage, totalItems, handlePageChange, setTotalAndReset } = usePagination(
    filterLocalStorage.page,
  );

  const [filterRestaurante, setFilterRestaurante] = useState(filterLocalStorage.location);
  const [filterChannels, setFilterChannels] = useState(filterLocalStorage?.channels ?? []);
  const selectedFormattedDates = {
    from: selectedDates.formattedFrom,
    to: selectedDates.formattedTo,
  };

  let limit = 10;
  let skip = 10 * ((currentPage ? currentPage : 1) - 1);

  const channelsQuery = useQuery({
    queryKey: [
      'channels',
      organization.id,
      selectedFormattedDates,
      limit,
      skip,
      filtersFormat(filterRestaurante),
      debouncedSearch,
      dateType,
      filtersFormat(filterChannels),
    ],
    queryFn: () =>
      getChannels(
        welletContext,
        limit,
        skip,
        organization.marketId,
        Number(organization.id),
        [],
        selectedFormattedDates,
        debouncedSearch.trim(),
        dateType.toLocaleUpperCase(),
        [],
        [],
        filtersFormat(filterRestaurante),
        [],
        orderBy,
        orderType,
        null,
        [],
        [],
        [],
        filtersFormat(filterChannels),
      ),
    onSuccess: (data) => {
      setTotalAndReset(data?.data.reservations?.totalRegisters);
      setFilterLocalStorage({
        ...filterLocalStorage,
        filterField: value,
        location: filterRestaurante,
        channels: filterChannels,
        page: currentPage,
      });
    },
    ...defaultQueryOptions,
    keepPreviousData: true,
  });

  const { getClientTranslation } = useGenericTranslation();

  const restaurantesQuery = useGetRestaurants(organization, {
    onSuccess: (data) => {
      let response = data?.data?.map((org) => ({
        label: org.name,
        value: org.id,
        isActive:
          filterLocalStorage?.location.length > 0
            ? filtersFormat(filterLocalStorage?.location).includes(org.id)
            : false,
      }));
      setFilterRestaurante(response);
    },
  });

  const channelsSimpleQuery = useGetChannels(organization, {
    onSuccess: (data) => {
      let response = data?.data?.map((c) => ({
        label: c.channel,
        value: c.id,
        isActive:
          filterLocalStorage?.channels.length > 0
            ? filtersFormat(filterLocalStorage?.channels).includes(c.id)
            : false,
      }));
      setFilterChannels(response);
    },
  });

  const headerAdmin = [
    { title: getClientTranslation('title.singular'), value: '', filterable: false },
    { title: t('table.channel'), value: '', filterable: false },
    { title: t('table.pax'), value: '', filterable: false, center: true },
    { title: t('table.commission'), value: '', filterable: false, center: true },
    { title: t('table.volume'), value: '', filterable: false },
  ];

  const downloadReservationMutation = useMutation({
    mutationFn: () => {
      const response = downloadChannels(welletContext, {
        organizationId: !organization.isMarket ? Number(organization.id) : null,
        marketSetupId: organization.marketId,
        from: selectedFormattedDates.from,
        to: selectedFormattedDates.to,
        status: [],
        filterField: debouncedSearch.trim(),
        dateType: dateType.toLocaleUpperCase(),
        userId: [],
        rpId: [],
        showId: filtersFormat(filterRestaurante),
        consumptionAmount: 0,
        consumptionFilterEnum: null,
        reservationType: null,
        citiesIds: [],
        channelId: filtersFormat(filterChannels),
      });

      return response;
    },
    onSuccess: (data) => downloadFileFromResponse(data, 'reservations.xlsx'),
  });

  return (
    <>
      <PageHeader title={t('title')} endComponent={<DateFilter isMultipleDate={false} />} />
      <Show>
        <Show.When
          isTrue={
            channelsQuery.isLoading || channelsSimpleQuery.isLoading || restaurantesQuery.isLoading
          }
        >
          <Loading />
        </Show.When>
        <Show.When isTrue={channelsQuery.isError}>
          <PageError />
        </Show.When>
        <Show.Else>
          <div className='row px-1 my-3'>
            <FilterCard
              category={t('cardFilter.totalPax')}
              quantity={channelsQuery?.data?.data?.totalPax}
              onClick={() => {}}
              className=' col-auto'
              isHoverable={false}
              isSelected={false}
            />
            <FilterCard
              category={t('cardFilter.ordersAmount')}
              quantity={channelsQuery?.data?.data?.totalOrdersAmount}
              onClick={() => {}}
              isSelected={false}
              minify={100000000}
              isHoverable={false}
              isMoney
              className=' col-auto'
            />
            <FilterCard
              category={t('cardFilter.activeChannels')}
              quantity={channelsQuery?.data?.data?.channels}
              onClick={() => {}}
              className=' col-auto'
              isHoverable={false}
              isSelected={false}
            />
          </div>
          <div className='d-flex justify-content-center justify-content-sm-start my-3 align-items-center'>
            <div className='col col-sm-auto align-items-center justify-content-center'>
              <Input
                icon={<SearchIcon color='currentColor' size='16px' />}
                type={'search'}
                customClass='mb-0 mr-3'
                name={'search'}
                value={value}
                onChangeValue={(e) => setValue(e.target.value)}
                placeholder={t('search')}
              />
            </div>
            <div className='row'>
              <div className='col-auto mb-1  pl-1 justify-content-center'>
                <ButtonFilter
                  isSearchable={true}
                  filterName={getClientTranslation('title.plural')}
                  filterOptions={filterRestaurante}
                  callback={setFilterRestaurante}
                  error={restaurantesQuery.isError}
                />
              </div>
              <ButtonFilter
                isSearchable={true}
                filterName={t('title')}
                filterOptions={filterChannels}
                callback={setFilterChannels}
                error={channelsSimpleQuery.isError}
              />

              {/* <div className='mb-1 pl-1 col-auto justify-content-center'>

              <div className='mb-1 pl-1 col-auto justify-content-center'>
                <ButtonFilter
                  isSearchable={true}
                  filterName={t('buttonsFilter.bossSeller')}
                  filterOptions={filterBoss}
                  callback={setFilterBoss}
                  error={bossQuery.isError}
                />
              </div> */}
            </div>
            <div className='col d-flex justify-content-end'>
              <Button
                className={'btn-wellet-secondary opacity justify-content-center'}
                size={'small'}
                text={t('download')}
                width={'36px'}
                disabled={downloadReservationMutation.isLoading}
                loading={downloadReservationMutation.isLoading}
                onClick={() => downloadReservationMutation.mutate()}
              />
            </div>
          </div>
          <Show>
            <Show.When isTrue={channelsQuery.isPreviousData}>
              <div className='my-4 py-4'>
                <Loading global={false} />
              </div>
            </Show.When>
            <Show.Else>
              <Table
                data={channelsQuery?.data?.data?.reservations?.items ?? []}
                hasPagination={false}
                totalP={totalItems}
                onPageChange={handlePageChange}
                currentPage={currentPage}
                columns={headerAdmin}
                onColumnClick={handleColumnClick}
                activeColumn={orderType}
                sortOrder={orderBy}
                rowClass='not-hover'
                renderRow={(reservations) => {
                  return (
                    <>
                      <TableCell style={{ width: '250px' }}>
                        <OrganizationCard
                          name={reservations?.restaurant?.name}
                          date={reservations?.restaurant?.cityName}
                          size={40}
                          imageUrl={reservations?.restaurant?.logo}
                          hightLightName={true}
                        />
                      </TableCell>
                      <TableCell  style={{ width: 'auto' }}>{reservations.channel}</TableCell>
                      <TableCell className={'text-center'} style={{ width: 'auto' }}>
                        {reservations.paxs}
                      </TableCell>
                      <TableCell
                        className={'text-center'}
                        style={{ width: 'auto' }}
                        type={'primary'}
                      >
                        {reservations.commission
                          ? `${reservations?.commission?.toFixed(2)} %`
                          : '0 %'}
                      </TableCell>
                      <TableCell style={{ width: 'auto' }} type={'primary'} >
                        {reservations.orderTotalAmount ? (
                          <Money value={reservations.orderTotalAmount} />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </>
                  );
                }}
              />
            </Show.Else>
          </Show>
        </Show.Else>
      </Show>
    </>
  );
};

export default Channels;
